import React, { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import Button from "../../../components/Button/Button";
import Card from "../../../components/Card/Card";
import DeleteButton from "../../../components/DeleteButton/DeleteButton";
import Input from "../../../components/Input/Input";
import List from "../../../components/List/List";
import ModalComponent from "../../../components/Modal/ModalComponent";
import Select from "../../../components/Select/Select";
import { useListManager } from "../../../contexts/list.manager";
import "../TabsCondoUsers/TabsCondoUsers.css";
import "../TabsCondoUsers/TabCredentials/TabCondoUsersCredentials.css";
import { v4 as uuidv4 } from 'uuid';
import { CREDENTIAL_TYPE_CODES, CREDENTIAL_TYPE_NAMES } from "../../../constants/credentials";
import { useSSEContext } from "../../../contexts/sse";
import { MdEdit } from "react-icons/md";
import { BiScan } from "react-icons/bi";
import { useFastCondoUserStore } from "./context";
import { useTranslation } from "react-i18next";

export function CredentialsStep({ ...props }) {
   const { t } = useTranslation();
   const { credentials, errors, isSubmitting, update } = useFastCondoUserStore();
   const [showModal, setShowModal] = useState(false);
   const [showDetectModal, setShowDetectModal] = useState(false);
   const [id, setId] = useState(null);
   const [type, setType] = useState(null);
   const [code, setCode] = useState("");
   const [detectType, setDetectType] = useState("");
   const [mode, setMode] = useState("");
   const [isDetecting, setIsDetecting] = useState(false);
   const { lineSelected, closeForm } = useListManager();
   const { sseReceived } = useSSEContext();

   useEffect(() => {
      if (!isDetecting || !sseReceived) return;
      const event = sseReceived.content;
      if (!event?.info) return;
      const eventCredentialTypes = CREDENTIAL_TYPE_CODES?.[event.info.trim()];
      if (
         eventCredentialTypes &&
         event.name_code === 9 &&
         event.result === 'Falha' &&
         eventCredentialTypes.includes(Number(detectType))
      ) {
         setIsDetecting(false);
         setShowDetectModal(false);
         setCode(event.data);
         setType(detectType);
         setMode("add");
         setShowModal(true);
         setDetectType("");
      }
   }, [sseReceived]);

   function validateCredential(payload) {
      if (!payload.type) {
         NotificationManager.warning(
            t("common.credentialTypeRequired"),
            `${t("common.warning")}!`,
         );
         return false;
      }
      if (!payload.code) {
         NotificationManager.warning(
            t("common.credentialCodeRequired"),
            `${t("common.warning")}!`,
         );
         return false;
      }
      if ([2, 3].includes(payload.type)) {
         if (!/^[0-9]+$/.test(payload.code)) {
            NotificationManager.warning(
               t("common.credentialPasswordsMustBeNumeric"),
               `${t("common.warning")}!`,
            );
            return false;
         }
      }
      return true;
   }

   async function saveNewCredentials() {
      let payload = {
         _id: uuidv4(),
         type: parseInt(type),
         code: code,
      };
      if (!validateCredential(payload)) return;
      const newCondoUserCredentials = [...credentials, payload];
      update("credentials", newCondoUserCredentials);
   }

   async function editCredentials() {
      const indexToUpdate = credentials.findIndex(
         (i) => i._id === id,
      );
      if (indexToUpdate !== -1) {
         const updatedPayload = {
            ...credentials[indexToUpdate],
            type: parseInt(type),
            code: code,
            condoUser: props.condoUser,
         };
         if (!validateCredential(updatedPayload)) return;
         const updatedCondoUserCredentials = credentials.map(
            (item, index) => {
               if (index === indexToUpdate) {
                  return updatedPayload;
               }
               return item;
            },
         );
         update("credentials", updatedCondoUserCredentials);
      }
   }

   async function removeCredentials(item) {
      const indexToRemove = credentials.findIndex(
         (i) => i._id === item._id,
      );
      if (indexToRemove !== -1) {
         const updatedCondoUserCredentials = [
            ...credentials.slice(0, indexToRemove),
            ...credentials.slice(indexToRemove + 1),
         ];
         update("credentials", updatedCondoUserCredentials);
         closeForm();
      }
   }

   const handleClickModalButton = (action) => {
      if (action === "confirm") {
         if (mode === "add") saveNewCredentials();
         if (mode === "edit") editCredentials();
      }
      setType("");
      setCode("");
      setId(null);
      setShowModal(false);
      closeForm();
   };

   const handleClickAdd = () => {
      setMode("add");
      setShowModal(true);
   };

   const handleClickEdit = (row) => {
      const item = row ?? lineSelected;
      if (!item) return;
      setMode("edit");
      setType(item.type);
      setCode(item.code);
      setId(item._id);
      setShowModal(true);
   };

   const handleClickRemove = (row) => {
      const item = row ?? lineSelected;
      if (!item) return;
      removeCredentials(item);
   };

   // Lógica para detecção com loading
   const handleDetect = () => {
      if (!detectType) {
         NotificationManager.warning(
            t("common.selectCredentialToDetect"),
            `${t("common.warning")}!`,
         );
         return;
      }
      // Inicia o loading e simula a detecção
      setIsDetecting(true);
      // Simulação de espera (por exemplo, 2 segundos)
      setTimeout(() => {
         setIsDetecting(false);
         setShowDetectModal(false);
         setDetectType("");
      }, 20000000);
   };

   function renderInputCode() {
      let inputType = "text";
      if ([2, 3].includes(type)) {
         inputType = "password";
      }

      return (
         <Input
            name="code"
            id="code"
            type={inputType}
            labelText={t("common.credentialCode")}
            value={code}
            onChange={({ target }) => setCode(target.value)}
            tooltipText={t("common.insertCredentialCode")}
            tooltipVisible={true}
            showablePassword={false}
            disabled={isSubmitting}
         />
      );
   }

   return (
      <div
         className="w-[90%] mx-auto!"
         style={{ display: "flex", alignItems: "center", flexDirection: "column" }}
      >
         {errors.credentials && errors.credentials.length > 0 && (
            <p className="text-center text-red-500! mb-4!">
               {errors.credentials.join('. ')}
            </p>
         )}
         <div className="tab-condo-users-buttons-container">
            <div className="tab-condo-users-buttons">
               <Button
                  action="add"
                  className="tab-condo-users-add button-white w-[200px]!"
                  label={t("common.addCredential")}
                  onClick={handleClickAdd}
                  disabled={isSubmitting}
               />
            </div>
            <Button
               icon={<BiScan size={18} />}
               className="button-white w-[120px]!"
               label={t("common.detectCredential")}
               onClick={() => setShowDetectModal(true)}
               disabled={isSubmitting}
            />
         </div>
         <div className="list-visibility w-full">
            <List
               selectable={false}
               collumsObjIndex={[
                  { collumnLabel: t("common.credentialType"), index: "type", type: "changeValue" },
                  { collumnLabel: t("common.credentialCode"), index: "code", custom: ({ row }) => [2, 3].includes(row.type) ? row.code.replace(/./g, '*') : row.code },
                  {
                     collumnLabel: t("common.actions"), type: "actions", custom: ({ row }) => {
                        return (
                           <div className="flex justify-end gap-4">
                              <button type="button" disabled={isSubmitting} className={`cursor-pointer hover:opacity-70 duration-100`} onClick={e => {
                                 handleClickEdit(row);
                              }}>
                                 <MdEdit size={18} />
                              </button>
                              <DeleteButton
                                 className="button-white tab-condo-users-buttons-visibility"
                                 handleDelete={() => handleClickRemove(row)}
                                 simple
                                 disabled={isSubmitting}
                              />
                           </div>
                        );
                     }
                  },
               ]}
               changeColumnValue={[
                  { old: 1, new: t("common.remoteControl") },
                  { old: 2, new: t("common.password") },
                  { old: 3, new: t("common.panicPassword") },
                  { old: 4, new: t("common.tag") },
                  { old: 5, new: "ID CtrlId" },
                  { old: 6, new: t("common.remoteCredential") },
                  { old: 7, new: "Tag Sem Parar" },
               ]}
               data={credentials}
            />
         </div>
         <div
            className="card-visibility"
            style={{ width: "100%", maxWidth: "500px" }}
         >
            {credentials?.length > 0 ? (
               credentials.map((credential, index) => (
                  <Card
                     key={index}
                     data={credential}
                     delete={() => handleClickRemove(credential)}
                     edit={() => handleClickEdit(credential)}
                  >
                     <p className="card-content-title">{credential.type}</p>
                     <p className="card-content">Código: {credential.code}</p>
                  </Card>
               ))
            ) : (
               <div
                  style={{
                     marginTop: "10px",
                     backgroundColor: "var(--textColor)",
                     padding: "10px",
                     textAlign: "center",
                  }}
               >
                  {t("common.noItemsFound")}
               </div>
            )}
         </div>
         <ModalComponent
            modalIsOpen={showModal}
            handleConfirm={() => handleClickModalButton("confirm")}
            handleCancel={() => handleClickModalButton("cancel")}
            modalText={t("common.addCredential")}
            buttonLabel1={t("common.confirm")}
         >
            <div className="modal-credentials">
               <Select
                  labelText={t("common.credentialType")}
                  name="type"
                  id="type"
                  value={type}
                  onChange={({ target }) => {
                     setType(Number(target.value));
                     setCode("");
                  }}
                  options={[
                     { value: 1, text: t("common.remoteControl") },
                     { value: 2, text: t("common.password") },
                     { value: 3, text: t("common.panicPassword") },
                     { value: 4, text: t("common.tag") },
                     { value: 5, text: "ID CtrlId" },
                     { value: 6, text: t("common.remoteCredential") },
                     { value: 7, text: "Tag Sem Parar" },
                  ]}
                  tooltipText={t("common.selectCredentialType")}
                  tooltipVisible={true}
               />
               {renderInputCode()}
            </div>
         </ModalComponent>

         {/* Modal de detecção */}
         <ModalComponent
            modalIsOpen={showDetectModal}
            handleConfirm={handleDetect}
            handleCancel={() => {
               setShowDetectModal(false);
               setIsDetecting(false);
               setDetectType("");
            }}
            modalText={isDetecting ? t("common.detectingCredential", { credential: t(CREDENTIAL_TYPE_NAMES[detectType]) }) : t("common.selectCredentialToDetect")}
            disableConfirm={isDetecting}
            buttonLabel1={t("common.detectCredential")}
         >
            {isDetecting ? (
               <div className="detect-loading">
                  <div className="spinner" />
               </div>
            ) : (
               <Select
                  labelText={t("common.credentialType")}
                  name="detectType"
                  id="detectType"
                  value={detectType}
                  onChange={({ target }) => setDetectType(target.value)}
                  options={[
                     { value: 1, text: t(CREDENTIAL_TYPE_NAMES[1]) },
                     { value: 2, text: t(CREDENTIAL_TYPE_NAMES[2]) },
                     { value: 3, text: t(CREDENTIAL_TYPE_NAMES[3]) },
                     { value: 4, text: t(CREDENTIAL_TYPE_NAMES[4]) },
                     { value: 5, text: t(CREDENTIAL_TYPE_NAMES[5]) },
                     { value: 6, text: t(CREDENTIAL_TYPE_NAMES[6]) },
                     { value: 7, text: t(CREDENTIAL_TYPE_NAMES[7]) },
                  ]}
                  tooltipText={t("common.selectCredentialToDetect")}
                  tooltipVisible={true}
               />
            )}
         </ModalComponent>
      </div>
   );
}